import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../config/ThemeContext";

function CodesList({ codes = [], setCodes, socket, matchId, setNumber, compact = false }) {
  const [editingUUID, setEditingUUID] = useState(null);
  const [editedCode, setEditedCode] = useState("");
  const [deletingUUID, setDeletingUUID] = useState(null);
  const [lastDeleted, setLastDeleted] = useState(null);
  const [deletedIndex, setDeletedIndex] = useState(null);
  const [undoTimeoutId, setUndoTimeoutId] = useState(null);
  const [selectedRally, setSelectedRally] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);

  const inputRef = useRef(null);
  const theme = useTheme();

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return "⌛";
      case "confirmed":
        return "✅";
      case "modified":
        return "⚠️";
      case "rejected":
        return "🚫";
      default:
        return null;
    }
  };

  // Auto-focus input during edit
  useEffect(() => {
    if (editingUUID && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingUUID]);

  // Global Escape key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (editingUUID) setEditingUUID(null);
        if (deletingUUID) setDeletingUUID(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editingUUID, deletingUUID]);

  useEffect(() => {
    if (!socket) return;

    const onScoutingDeleted = ({ uuid }) => {
      if (uuid) {
        setCodes((prev) => prev.filter((c) => c.uuid !== uuid));
      }
    };

    const onScoutingError = ({ uuid, error }) => {
      if (!uuid) return;
      setCodes((prev) => {
        const idx = prev.findIndex((c) => c.uuid === uuid);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], status: "rejected" };
        console.error("🚫 Scouting entry rejected", uuid, error);
        return copy;
      });
    };

    socket.on("scoutingDeleted", onScoutingDeleted);
    socket.on("scoutingError", onScoutingError);
    return () => {
      socket.off("scoutingDeleted", onScoutingDeleted);
      socket.off("scoutingError", onScoutingError);
    };
  }, [socket, setCodes]);

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return "00:00";
    
    // Calculate hours, minutes, and seconds
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    // If there are hours, show HH:MM:SS format
    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    
    // Otherwise show MM:SS format
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleEdit = (uuid) => {
    const entry = codes.find((e) => e.uuid === uuid);
    if (!entry) return;
    setEditingUUID(uuid);
    setEditedCode(entry.actionCode);
  };

  const handleSaveEdit = () => {
    const updatedEntry = {
      ...codes.find((c) => c.uuid === editingUUID),
      actionCode: editedCode,
    };
    setCodes((prev) =>
      prev.map((c) => (c.uuid === editingUUID ? updatedEntry : c))
    );
    socket.emit("editScoutingAction", { matchId, setNumber, updatedEntry });
    setEditingUUID(null);
    setEditedCode("");
  };

  const handleDeleteRequest = (uuid) => {
    setDeletingUUID(uuid);
  };

  const confirmDelete = (uuid) => {
    const entryIndex = [...codes].reverse().findIndex((e) => e.uuid === uuid);
    const deletedEntry = codes.find((e) => e.uuid === uuid);
    
    // Immediately remove from local state only
    setCodes((prev) => prev.filter((e) => e.uuid !== uuid));
    
    // Save deleted entry info for potential undo
    setLastDeleted(deletedEntry);
    setDeletedIndex(entryIndex);

    // Set a 5-second timeout for both the undo UI and actual Redis deletion
    const timeout = setTimeout(() => {
      // Only emit delete to Redis if the entry is still deleted (hasn't been undone)
      if (lastDeleted?.uuid === uuid) {
        socket.emit("deleteScoutingAction", {
          matchId,
          setNumber,
          scoutingEntry: deletedEntry,
        });
      }
      setLastDeleted(null);
      setDeletedIndex(null);
      setUndoTimeoutId(null);
    }, 5000);

    setUndoTimeoutId(timeout);
    setDeletingUUID(null);
  };

  const cancelDelete = () => {
    setDeletingUUID(null);
  };

  const undoDelete = () => {
    if (lastDeleted) {
      // Restore to local state
      setCodes((prev) => [...prev, lastDeleted]);
      
      // Clear the deletion timeout to prevent Redis deletion
      clearTimeout(undoTimeoutId);
      setLastDeleted(null);
      setDeletedIndex(null);
      setUndoTimeoutId(null);
    }
  };

  // Group codes by rally
  const groupedCodes = codes.reduce((acc, code) => {
    const rallyId = code.rallyId || 0;
    if (!acc[rallyId]) {
      acc[rallyId] = [];
    }
    acc[rallyId].push(code);
    return acc;
  }, {});

  const sortedRallies = Object.keys(groupedCodes).sort((a, b) => Number(b) - Number(a));
  if (compact) {
    const displayRallies = showAllEvents ? sortedRallies : sortedRallies.slice(0, 3);
    
    return (
      <div className="h-full flex flex-col">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-700">
            📝 Events ({codes.length})
          </h3>
        </div>

        {/* Undo Snackbar */}
        {lastDeleted && (
          <div className="mb-2 p-2 rounded bg-blue-50 border border-blue-200 text-blue-700 text-xs flex-shrink-0">
            <div className="flex items-center justify-between">
              <span>Event deleted</span>
              <button
                onClick={undoDelete}
                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Undo
              </button>
            </div>
          </div>
        )}

        {/* Compact Events List */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {displayRallies.map((rallyId) => {
            const rallyCodes = groupedCodes[rallyId];
            const isEvenRally = Number(rallyId) % 2 === 0;

            return (
              <div
                key={rallyId}
                className={`
                  rounded-lg border transition-all duration-200
                  ${isEvenRally 
                    ? 'border-blue-200 bg-blue-50/50' 
                    : 'border-orange-200 bg-orange-50/50'
                  }
                  hover:shadow-sm
                `}
              >
                {/* Rally Header */}
                <div className={`
                  px-2 py-1 rounded-t-lg text-xs font-semibold
                  ${isEvenRally 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-orange-100 text-orange-800'
                  }
                  flex items-center justify-between
                `}>
                  <div className="flex items-center gap-1">
                    <span>{isEvenRally ? '🏐' : '⚡'}</span>
                    <span>Rally {rallyId}</span>
                    <span className="opacity-75">({rallyCodes.length})</span>
                  </div>
                  <span className="opacity-75">
                    {formatTime(rallyCodes[0]?.matchTimeElapsed || 0)}
                  </span>
                </div>

                {/* Rally Events */}
                <div className="p-2 space-y-1">
                  {rallyCodes.map((entry, index) => (
                    <div
                      key={entry.uuid}
                      className={`
                        flex items-center justify-between p-1 rounded
                        ${isEvenRally 
                          ? 'bg-blue-100/30 border border-blue-200' 
                          : 'bg-orange-100/30 border border-orange-200'
                        }
                        text-xs
                      `}
                    >
                      {editingUUID === entry.uuid ? (
                        <div className="flex items-center gap-1 w-full">
                          <input
                            ref={inputRef}
                            type="text"
                            value={editedCode}
                            onChange={(e) => setEditedCode(e.target.value)}
                            className="flex-1 px-1 py-1 rounded border border-blue-500 text-xs font-mono"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") setEditingUUID(null);
                            }}
                          />
                          <button
                            onClick={handleSaveEdit}
                            className="px-1 py-1 bg-green-600 text-white rounded text-xs"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditingUUID(null)}
                            className="px-1 py-1 bg-gray-600 text-white rounded text-xs"
                          >
                            ✗
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <div className={`
                              w-5 h-5 rounded-full flex items-center justify-center
                              text-xs font-bold text-white flex-shrink-0
                              ${isEvenRally ? 'bg-blue-500' : 'bg-orange-500'}
                            `}>
                              {index + 1}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-500">
                                  {formatTime(entry.matchTimeElapsed)}
                                </span>
                                <span className="font-mono">
                                  {entry.actionCode}
                                </span>
                                {getStatusIcon(entry.status) && (
                                  <span>{getStatusIcon(entry.status)}</span>
                                )}
                                {entry.scoreText && (
                                  <span className="text-green-600 font-bold whitespace-nowrap">
                                    🏅 {entry.scoreText}
                                  </span>
                                )}
                                {entry.isRotation && (
                                  <span className="text-gray-600 whitespace-nowrap">
                                    🔄 Rotation
                                  </span>
                                )}
                                {entry.isServiceChange && (
                                  <span className="text-gray-600 whitespace-nowrap">
                                    ↔️ Service Change
                                  </span>
                                )}
                              </div>
                              {entry.description && (
                                <div className="text-gray-600 mt-0.5 break-words">
                                  {entry.description}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <button
                              onClick={() => handleEdit(entry.uuid)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteRequest(entry.uuid)}
                              className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Show More/Less Button */}
          {sortedRallies.length > 3 && (
            <div className="mt-3 flex justify-center border-t border-gray-200 pt-3">
              <button
                onClick={() => setShowAllEvents(prev => !prev)}
                className="
                  group px-3 py-1.5 rounded-lg
                  bg-gray-100 hover:bg-gray-200
                  text-gray-700 font-medium text-sm
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-gray-300
                  flex items-center gap-2
                "
              >
                <span>{showAllEvents ? 'Show Recent' : `Show All Rallies (${sortedRallies.length})`}</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${showAllEvents ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deletingUUID && (
          <div className="
            fixed inset-0 bg-black/50 flex items-center justify-center z-50
            backdrop-blur-sm
          ">
            <div className="
              bg-white rounded-lg p-4 max-w-sm mx-4
              shadow-xl border border-gray-200
            ">
              <h3 className="text-lg font-bold mb-2 text-red-600">
                Delete Event?
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                Are you sure you want to delete this scouting event?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDelete(deletingUUID)}
                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full version (original design)
  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-800">
          📝 Scouting Events
        </h3>
        <div className="text-sm text-gray-500">
          {codes.length} total events
        </div>
      </div>

      {/* Undo Snackbar */}
      {lastDeleted && (
        <div className="
          mb-4 p-4 rounded-xl
          bg-gradient-to-r from-blue-500 to-purple-600
          text-white shadow-lg
          animate-in slide-in-from-top-2
        ">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">🔄</span>
              <span className="font-medium">Event deleted</span>
            </div>
            <button
              onClick={undoDelete}
              className="
                px-4 py-2 bg-white/20 hover:bg-white/30
                rounded-lg font-semibold transition-all duration-200
                backdrop-blur-sm border border-white/30
                hover:scale-105
              "
            >
              Undo
            </button>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
        {sortedRallies.map((rallyId) => {
          const rallyCodes = groupedCodes[rallyId];
          const isEvenRally = Number(rallyId) % 2 === 0;
          const isSelected = selectedRally === rallyId;

          return (
            <div
              key={rallyId}
              className={`
                rounded-xl border-2 transition-all duration-300
                ${isEvenRally 
                  ? 'border-blue-200 bg-blue-50/50' 
                  : 'border-orange-200 bg-orange-50/50'
                }
                ${isSelected ? 'ring-4 ring-blue-300 scale-[1.02]' : ''}
                hover:shadow-lg hover:scale-[1.01]
              `}
              onClick={() => setSelectedRally(isSelected ? null : rallyId)}
            >
              {/* Rally Header */}
              <div className={`
                px-4 py-3 rounded-t-xl font-semibold
                ${isEvenRally 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-orange-100 text-orange-800'
                }
                flex items-center justify-between
              `}>
                <div className="flex items-center gap-3">
                  <span className="text-lg">
                    {isEvenRally ? '🏐' : '⚡'}
                  </span>
                  <span>Rally {rallyId}</span>
                  <span className="text-sm opacity-75">
                    ({rallyCodes.length} events)
                  </span>
                </div>
                <div className="text-sm opacity-75">
                  {formatTime(rallyCodes[0]?.matchTimeElapsed || 0)}
                </div>
              </div>

              {/* Rally Events */}
              <div className="p-4 space-y-2">
                {rallyCodes.map((entry, index) => (
                  <div
                    key={entry.uuid}
                    className={`
                      flex items-center justify-between p-3 rounded-lg
                      ${isEvenRally 
                        ? 'bg-blue-100/50 border border-blue-200' 
                        : 'bg-orange-100/50 border border-orange-200'
                      }
                      transition-all duration-200 hover:shadow-md
                    `}
                  >
                    {editingUUID === entry.uuid ? (
                      <div className="flex items-center gap-3 w-full">
                        <input
                          ref={inputRef}
                          type="text"
                          value={editedCode}
                          onChange={(e) => setEditedCode(e.target.value)}
                          className="
                            flex-1 px-3 py-2 rounded-lg border-2
                            border-blue-500 focus:ring-4 focus:ring-blue-300
                            focus:outline-none font-mono
                          "
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit();
                            if (e.key === "Escape") setEditingUUID(null);
                          }}
                        />
                        <button
                          onClick={handleSaveEdit}
                          className="
                            px-3 py-2 bg-green-600 hover:bg-green-700
                            text-white rounded-lg font-semibold
                            transition-all duration-200
                          "
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUUID(null)}
                          className="
                            px-3 py-2 bg-gray-600 hover:bg-gray-700
                            text-white rounded-lg font-semibold
                            transition-all duration-200
                          "
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div className={`
                            w-8 h-8 rounded-full flex items-center justify-center
                            text-sm font-bold text-white
                            ${isEvenRally ? 'bg-blue-500' : 'bg-orange-500'}
                          `}>
                            {index + 1}
                          </div>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-xs">
                                {formatTime(entry.matchTimeElapsed)}
                              </span>
                              <span className="font-mono font-semibold">
                                {entry.actionCode}
                              </span>
                              {getStatusIcon(entry.status) && (
                                <span>{getStatusIcon(entry.status)}</span>
                              )}
                              {entry.scoreText && (
                                <span className="text-gray-600">
                                  🏅 {entry.scoreText}
                                </span>
                              )}
                              {entry.isRotation && (
                                <span className="text-gray-600">
                                  🔄 Rotation
                                </span>
                              )}
                              {entry.isServiceChange && (
                                <span className="text-gray-600">
                                  ↔️ Service Change
                                </span>
                              )}
                            </div>
                            {entry.description && (
                              <div className="text-gray-600 text-xs mt-0.5">
                                {entry.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(entry.uuid)}
                            className="
                              p-2 text-blue-600 hover:bg-blue-100
                              rounded-lg transition-all duration-200
                              hover:scale-110
                            "
                            title="Edit event"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(entry.uuid)}
                            className="
                              p-2 text-red-600 hover:bg-red-100
                              rounded-lg transition-all duration-200
                              hover:scale-110
                            "
                            title="Delete event"
                          >
                            🗑️
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {codes.length === 0 && (
          <div className="
            text-center py-12 text-gray-500
            bg-gray-50 rounded-xl border-2 border-dashed border-gray-300
          ">
            <div className="text-4xl mb-4">📝</div>
            <div className="text-lg font-semibold mb-2">No events yet</div>
            <div className="text-sm">Start scouting to see events here</div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingUUID && (
        <div className="
          fixed inset-0 bg-black/50 flex items-center justify-center z-50
          backdrop-blur-sm
        ">
          <div className="
            bg-white rounded-2xl p-6 max-w-md mx-4
            shadow-2xl border border-gray-200
          ">
            <h3 className="text-xl font-bold mb-4 text-red-600">
              Delete Event?
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this scouting event? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="
                  flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300
                  rounded-lg font-semibold transition-all duration-200
                "
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(deletingUUID)}
                className="
                  flex-1 px-4 py-2 bg-red-600 hover:bg-red-700
                  text-white rounded-lg font-semibold transition-all duration-200
                "
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CodesList;
